<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const inboxId = computed(() => route.params.inboxId);
const currentInbox = computed(() => {
  const inboxes = getters['inboxes/getInboxes'].value;
  return inboxes.find(inbox => inbox.id === parseInt(inboxId.value));
});

const conversionCondition = ref('');
const isLoading = ref(false);
const isSaving = ref(false);

// Carrega a condição atual ao montar o componente
onMounted(async () => {
  isLoading.value = true;
  try {
    const accountId = route.params.accountId;
    const response = await window.axios.get(`/api/v1/accounts/${accountId}/config_ouroboros/inbox/${inboxId.value}/conversion_condition`);
    conversionCondition.value = response.data.conversion_condition || '';
  } catch (error) {
    console.error('Error loading conversion condition:', error);
  } finally {
    isLoading.value = false;
  }
});

const saveCondition = async () => {
  isSaving.value = true;
  try {
    const accountId = route.params.accountId;
    await window.axios.put(`/api/v1/accounts/${accountId}/config_ouroboros/inbox/${inboxId.value}/conversion_condition`, {
      conversion_condition: conversionCondition.value,
    });

    useAlert(t('OUROBOROS.EDIT.API.SUCCESS_MESSAGE'));
    router.push({ name: 'settings_ouroboros_list' });
  } catch (error) {
    console.error('Error saving conversion condition:', error);
    useAlert(t('OUROBOROS.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'settings_ouroboros_list' });
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('OUROBOROS.EDIT.HEADER')"
        :description="$t('OUROBOROS.EDIT.DESCRIPTION', { inboxName: currentInbox?.name })"
      />
    </template>
    <template #body>
      <div class="max-w-4xl">
        <div class="mb-6">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
            {{ $t('OUROBOROS.EDIT.CONDITION_LABEL') }}
          </label>
          <textarea
            v-model="conversionCondition"
            class="w-full px-3 py-2 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            rows="10"
            :placeholder="$t('OUROBOROS.EDIT.CONDITION_PLACEHOLDER')"
          />
          <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {{ $t('OUROBOROS.EDIT.CONDITION_HELP') }}
          </p>
        </div>

        <div class="flex gap-2">
          <woot-button
            :loading="isSaving"
            @click="saveCondition"
          >
            {{ $t('OUROBOROS.EDIT.SAVE') }}
          </woot-button>
          <woot-button
            variant="clear"
            color-scheme="secondary"
            @click="goBack"
          >
            {{ $t('OUROBOROS.EDIT.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>